import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Flag, BarChart3 } from "lucide-react";
import GameRulesButton from "@/components/GameRulesButton";
import { formatForRound } from "@/game/rules";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CadiiChat } from "@/components/CadiiChat";

export default function PlayRound() {
  const { roundId } = useParams();
  const navigate = useNavigate();
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scores, setScores] = useState({});
  const [doublesDismissed, setDoublesDismissed] = useState(false);
  const [settingDoubles, setSettingDoubles] = useState(false);

  const loadRound = useCallback(async () => {
    try {
      const res = await fetch(`${API}/v2/rounds/${roundId}`, { credentials: "include" });
      if (!res.ok) throw new Error("not found");
      const data = await res.json();
      setRound(data);
      // Pre-fill scores for current hole if already entered
      const holeScores = data.hole_scores?.[String(data.current_hole)] || {};
      setScores(holeScores);
    } catch {
      toast.error("Ronda no encontrada");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [roundId, navigate]);

  useEffect(() => { loadRound(); }, [loadRound]);

  const goToHole = (hole) => {
    const holeScores = round.hole_scores?.[String(hole)] || {};
    setScores(holeScores);
    setRound(prev => ({ ...prev, current_hole: hole }));
  };

  const updateScore = (playerId, value) => {
    setScores(prev => ({ ...prev, [playerId]: value === "" ? "" : parseInt(value) || 0 }));
  };

  const saveHole = async () => {
    const hole = round.current_hole;
    const holeScores = Object.entries(scores).filter(([, v]) => v > 0).map(([pid, s]) => ({ player_id: pid, strokes: s }));
    if (holeScores.length === 0) { toast.error("Ingresa al menos un score"); return; }

    setSaving(true);
    try {
      const res = await fetch(`${API}/v2/rounds/${roundId}/scores`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ hole, scores: holeScores }),
      });
      if (!res.ok) throw new Error("Error");
      const updated = await res.json();
      setRound(updated);
      // Move to next hole
      const nextScores = updated.hole_scores?.[String(updated.current_hole)] || {};
      setScores(nextScores);
      if (updated.status === "completed") {
        toast.success("Ronda completada!");
        navigate(`/play/${roundId}/results`);
      }
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  if (loading || !round) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7F3EA]"><div className="w-8 h-8 border-3 border-[#103B31] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const hole = round.current_hole;
  const courseHoles = round.course?.holes || [];
  const holeInfo = courseHoles.find(h => h.hole === hole);
  const holePar = holeInfo?.par || 4;
  const holeYards = holeInfo?.yards || 0;
  const players = round.players || [];
  const totalPar = courseHoles.reduce((s, h) => s + h.par, 0);

  // Hole navigation
  const starting = round.starting_hole || 1;
  const holesCount = round.holes_count || 18;
  const holeOrder = [...Array.from({ length: 18 - starting + 1 }, (_, i) => starting + i), ...(starting > 1 ? Array.from({ length: starting - 1 }, (_, i) => i + 1) : [])].slice(0, holesCount);
  const completedHoles = new Set(Object.keys(round.hole_scores || {}).map(Number));
  const holeIdx = holeOrder.indexOf(hole);

  const isComplete = round.status === "completed";

  const front9Done = holeOrder.slice(0, 9).every(h => completedHoles.has(h));
  const hasModules = Object.keys(round.active_modules || {}).length > 0;
  const doublesAvailable = front9Done && hasModules && !round.doubles_decision && !isComplete;

  const setDoubles = async (doubled) => {
    setSettingDoubles(true);
    try {
      const res = await fetch(`${API}/v2/rounds/${roundId}/doubles`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ doubled }),
      });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setRound(updated);
      toast.success(doubled ? "¡Vuelta doblada! Los juegos valen x2" : "La vuelta se queda igual");
    } catch { toast.error("Error al guardar"); }
    finally { setSettingDoubles(false); setDoublesDismissed(true); }
  };

  return (
    <div className="min-h-screen bg-[#F7F3EA] text-[#102E32]" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="play-round-page">

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#DED5C2] bg-white/60">
        <button onClick={() => navigate("/dashboard")} className="text-[#6B7A6F] hover:text-[#102E32]"><ArrowLeft size={20} /></button>
        <span className="text-sm font-medium text-[#3D4A42] truncate max-w-[200px]">{round.name}</span>
        <div className="flex items-center gap-2">
          <GameRulesButton formatId={formatForRound(round)} />
          {round.doubled_back9 && (
            <span className="text-[10px] font-black text-[#103B31] bg-[#103B31]/10 px-2 py-0.5 rounded-full" data-testid="doubled-badge">x2 Vuelta</span>
          )}
          <span className="text-xs text-[#9AA69C]">{round.course?.course_name || ""}</span>
        </div>
      </div>

      {/* Hole selector strip */}
      <div className="flex overflow-x-auto gap-1 px-3 py-2 border-b border-[#DED5C2] no-scrollbar">
        {holeOrder.map(h => {
          const done = completedHoles.has(h);
          const active = h === hole;
          return (
            <button key={h} onClick={() => goToHole(h)}
              className={`flex-shrink-0 w-9 h-9 rounded-full text-xs font-bold flex items-center justify-center transition-all
                ${active ? "bg-[#103B31] text-white scale-110" : done ? "bg-[#103B31]/10 text-[#103B31]" : "bg-[#F1EBDD] text-[#9AA69C]"}`}
              data-testid={`hole-nav-${h}`}>
              {done && !active ? <Check size={12} /> : h}
            </button>
          );
        })}
      </div>

      {/* Current hole info */}
      <div className="text-center py-6 px-4">
        <p className="text-xs uppercase tracking-widest text-[#9AA69C] mb-1">Hoyo</p>
        <p className="text-6xl font-black text-[#102E32] mb-1" data-testid="current-hole-number">{hole}</p>
        <div className="flex items-center justify-center gap-4 text-sm text-[#6B7A6F]">
          <span>Par {holePar}</span>
          {holeYards > 0 && <span>{holeYards} yds</span>}
          {holeInfo?.handicap_index && <span>HCP {holeInfo.handicap_index}</span>}
        </div>
      </div>

      {/* Score inputs */}
      <div className="px-4 space-y-3 pb-6" data-testid="score-inputs">
        {players.map(p => {
          const val = scores[p.player_id];
          const diff = val && holePar ? val - holePar : null;
          let diffLabel = "";
          let diffColor = "text-[#ADB8AE]";
          if (diff !== null && val > 0) {
            if (diff <= -2) { diffLabel = `${diff}`; diffColor = "text-amber-500"; }
            else if (diff === -1) { diffLabel = "-1"; diffColor = "text-blue-600"; }
            else if (diff === 0) { diffLabel = "E"; diffColor = "text-green-600"; }
            else if (diff === 1) { diffLabel = "+1"; diffColor = "text-amber-600"; }
            else { diffLabel = `+${diff}`; diffColor = "text-red-600"; }
          }

          // Player total so far
          const playerTotal = Object.values(round.hole_scores || {}).reduce((sum, hs) => sum + (hs[p.player_id] || 0), 0);

          return (
            <div key={p.player_id} className="flex items-center gap-3 bg-white rounded-xl border border-[#DED5C2] shadow-sm px-4 py-3" data-testid={`score-input-${p.player_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#102E32] truncate">{p.name}</p>
                <p className="text-[10px] text-[#9AA69C]">HCP {p.handicap_index}{playerTotal > 0 ? ` | Total: ${playerTotal}` : ""}</p>
              </div>
              {diff !== null && val > 0 && <span className={`text-xs font-bold ${diffColor}`}>{diffLabel}</span>}
              <div className="flex items-center gap-1">
                <button onClick={() => updateScore(p.player_id, Math.max(1, (parseInt(val) || holePar) - 1))}
                  className="w-10 h-10 rounded-lg bg-[#F1EBDD] text-[#102E32] font-bold text-lg flex items-center justify-center active:bg-[#E9E1CE]">
                  -
                </button>
                <input type="number" value={val ?? ""} onChange={e => updateScore(p.player_id, e.target.value)}
                  className="w-14 h-10 bg-white border border-[#DED5C2] rounded-lg text-center text-lg font-bold text-[#102E32] focus:outline-none focus:ring-2 focus:ring-[#103B31]"
                  data-testid={`strokes-${p.player_id}`} />
                <button onClick={() => updateScore(p.player_id, (parseInt(val) || holePar) + 1)}
                  className="w-10 h-10 rounded-lg bg-[#F1EBDD] text-[#102E32] font-bold text-lg flex items-center justify-center active:bg-[#E9E1CE]">
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Save + Navigate */}
      <div className="px-4 pb-8 space-y-3">
        <button onClick={saveHole} disabled={saving}
          className="w-full py-4 rounded-xl bg-[#103B31] hover:bg-[#092B25] text-white font-bold text-base flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-[0.98]"
          data-testid="save-hole-btn">
          {saving ? "Guardando..." : isComplete ? (
            <><Flag size={16} /> Ver Resultados</>
          ) : (
            <><Check size={16} /> Guardar Hoyo {hole}</>
          )}
        </button>

        {/* Quick nav */}
        <div className="flex gap-2">
          {holeIdx > 0 && (
            <button onClick={() => goToHole(holeOrder[holeIdx - 1])}
              className="flex-1 py-2.5 rounded-lg bg-[#F1EBDD] text-[#6B7A6F] text-sm font-medium flex items-center justify-center gap-1">
              <ArrowLeft size={14} /> Hoyo {holeOrder[holeIdx - 1]}
            </button>
          )}
          {holeIdx < holeOrder.length - 1 && (
            <button onClick={() => goToHole(holeOrder[holeIdx + 1])}
              className="flex-1 py-2.5 rounded-lg bg-[#F1EBDD] text-[#6B7A6F] text-sm font-medium flex items-center justify-center gap-1">
              Hoyo {holeOrder[holeIdx + 1]} <ArrowRight size={14} />
            </button>
          )}
        </div>

        {/* Live results */}
        <button onClick={() => navigate(`/play/${roundId}/results`)}
          className="w-full py-3 rounded-xl bg-[#F1EBDD] border border-[#DED5C2] text-[#3D4A42] text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#F1EBDD] transition-colors"
          data-testid="view-results-btn">
          <BarChart3 size={16} className="text-[#103B31]" /> Ver Resultados
        </button>

        {/* Doubles reminder (if dismissed without deciding) */}
        {doublesAvailable && doublesDismissed && (
          <button onClick={() => setDoublesDismissed(false)}
            className="w-full py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-600 text-sm font-bold"
            data-testid="doubles-reminder-btn">
            ¿Doblar apuestas en la vuelta? x2
          </button>
        )}

        {/* Running total */}
        {Object.keys(round.player_totals || {}).length > 0 && (
          <div className="bg-white rounded-xl border border-[#DED5C2] shadow-sm p-3 mt-4">
            <p className="text-[10px] uppercase tracking-wider text-[#ADB8AE] mb-2">Total ({completedHoles.size}/{holesCount} hoyos)</p>
            <div className="space-y-1">
              {players.map(p => {
                const total = round.player_totals?.[p.player_id] || 0;
                const vsPar = total > 0 && totalPar > 0 ? total - (totalPar * completedHoles.size / 18) : 0;
                return (
                  <div key={p.player_id} className="flex items-center justify-between text-sm">
                    <span className="text-[#4B5A50]">{p.name}</span>
                    <span className="font-bold text-[#102E32]">{total > 0 ? total : "—"}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Doubles prompt after front 9 */}
      <Dialog open={doublesAvailable && !doublesDismissed} onOpenChange={(o) => !o && setDoublesDismissed(true)}>
        <DialogContent className="bg-white border-[#DED5C2] text-[#102E32] max-w-sm" style={{ fontFamily: "Manrope, sans-serif" }}>
          <DialogHeader>
            <DialogTitle className="text-center text-2xl font-black">¿Doblar la vuelta?</DialogTitle>
            <DialogDescription className="text-center text-sm text-[#8A968C]">
              Terminaron los primeros 9. Si doblan, los juegos por hoyo (match play, skins, unidades, oyes) valen <span className="text-[#103B31] font-bold">x2</span> en los segundos 9.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-2">
            <button onClick={() => setDoubles(false)} disabled={settingDoubles}
              className="flex-1 py-3.5 rounded-xl bg-[#F1EBDD] text-[#4B5A50] font-semibold disabled:opacity-50"
              data-testid="decline-doubles-btn">
              No, seguir igual
            </button>
            <button onClick={() => setDoubles(true)} disabled={settingDoubles}
              className="flex-1 py-3.5 rounded-xl bg-[#103B31] text-white font-black text-lg disabled:opacity-50"
              data-testid="confirm-doubles-btn">
              ¡Doblar x2!
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <CadiiChat roundId={roundId} />
    </div>
  );
}
